// Code generated by payloadgen (export-payload-types); DO NOT EDIT.
// Source of truth: the exported structs of Go package "api".
// Regenerated by scripts/generate.ts on every install — never committed.

/** MultipartFieldJSON carries the JSON-encoded request struct. */
export const MultipartFieldJSON = 'json'

/** MultipartFieldAttachments carries the attachment files, one part each. */
export const MultipartFieldAttachments = 'attachments'

/**
 * Recipient is one name/address pair. Field-compatible with the provider-side
 * mailer.Recipient; the server converts at the provider boundary.
 */
export interface Recipient {
    name: string
    email: string
}

/** SendEmailRequest is the body of POST /api/mail/send. */
export interface SendEmailRequest {
    mailbox_id: string
    alias_id?: string
    to: Recipient[]
    cc?: Recipient[]
    bcc?: Recipient[]
    subject: string
    html_body: string
    text_body: string
    /**
     * InReplyToMessageID is the PocketBase record ID of the message being
     * replied to — not an RFC 822 Message-ID header value.
     */
    in_reply_to_message_id?: string
}

/** SaveDraftRequest is the body of POST /api/mail/draft. */
export interface SaveDraftRequest {
    mailbox_id: string
    alias_id?: string
    /**
     * MessageID is the existing draft's PocketBase record ID; empty creates
     * a new draft.
     */
    message_id?: string
    to?: Recipient[]
    cc?: Recipient[]
    bcc?: Recipient[]
    subject?: string
    html_body: string
    text_body: string
}

/**
 * SendEmailResponse is returned by both POST /api/mail/send and
 * POST /api/mail/draft. MessageID and ThreadID are PocketBase record IDs of
 * the stored message and its thread — MessageID is not the RFC 822
 * Message-ID header.
 */
export interface SendEmailResponse {
    message_id: string
    thread_id: string
}

/**
 * SearchRequest names the query parameters of GET /api/mail/search. It is a
 * query-string contract, not a JSON body: every value travels as a string and
 * the tags name the parameter keys. Limit defaults to 25 (max 100); Query
 * terms shorter than 2 characters are ignored.
 */
export interface SearchRequest {
    q?: string
    mailbox_id?: string
    limit?: number
    offset?: number
    from?: string
    to?: string
    subject?: string
    has_words?: string
    not?: string
    date_after?: string
    date_before?: string
    folder?: string
    has_attachment?: boolean
}

/**
 * SearchResultItem is one thread-level hit in a SearchResponse. Highlight
 * fields carry <mark> markup from FTS; Participants is the thread's stored
 * JSON participant list, passed through as a string.
 */
export interface SearchResultItem {
    thread_id: string
    subject: string
    subject_highlight: string
    snippet_highlight: string
    latest_date: string
    participants: string
    message_count: number
    mailbox_id: string
    has_attachments: boolean
}

/** SearchResponse is returned by GET /api/mail/search. Items is never null. */
export interface SearchResponse {
    items: SearchResultItem[]
    total: number
}

/**
 * MXCheckResult reports whether the domain's MX records point at the
 * provider's expected inbound host.
 */
export interface MXCheckResult {
    ok: boolean
    expected: string
    actual?: string[]
    error?: string
}

/** ProviderCheckResult records the provider-side inbound configuration check. */
export interface ProviderCheckResult {
    ok: boolean
    expected_domain?: string
    server_domain?: string
    inbound_address?: string
    error?: string
}

/**
 * OutboundCheckResult reports outbound deliverability: whether the provider
 * has the domain enrolled, whether SPF/DKIM/return-path verify, and the DNS
 * records the admin must publish to make them verify.
 */
export interface OutboundCheckResult {
    spf: boolean
    dkim: boolean
    return_path: boolean
    /**
     * Enrolled is "yes", "no" or "unknown". Three-valued because a check that
     * could not run must not look like a domain the provider rejected — the
     * UI phrases them differently and only "no" is the admin's to fix.
     */
    enrolled?: string
    /**
     * MXHost is the expected inbound MX target (expectedInboundMXHost) — the
     * value the admin must point an MX record at. Carried here, not just in
     * VerificationDetails.MX.Expected, so the enrollment response (which never
     * runs the MX check — see handleAddDomain) can still tell the client what
     * to render before the first Verify.
     */
    mx_host?: string
    /**
     * MXVerified mirrors VerificationDetails.MX.OK, so the DNS table can mark
     * the MX row from the same object that carries MXHost. False until the
     * first Verify: enrollment never runs the MX check.
     */
    mx_verified: boolean
    dkim_host?: string
    dkim_text_value?: string
    return_path_domain?: string
    return_path_cname_value?: string
    error?: string
}

/**
 * VerificationDetails is the full per-check breakdown of a domain
 * verification run, persisted on mail_domains.verification_details and
 * returned by the verify endpoint.
 */
export interface VerificationDetails {
    mx: MXCheckResult
    provider: ProviderCheckResult
    outbound: OutboundCheckResult
    provider_configured: boolean
    provider_name?: string
}

/**
 * VerifyDomainResponse is returned by POST /api/mail/domains/{id}/verify.
 * The verification checks always ran when this arrives; Saved reports whether
 * their outcome could be persisted. A client must treat Saved == false as a
 * failure (the record still holds the previous state) — SaveError carries the
 * reason.
 */
export interface VerifyDomainResponse {
    id: string
    verified: boolean
    mx_verified: boolean
    inbound_domain_verified: boolean
    spf_verified: boolean
    dkim_verified: boolean
    return_path_verified: boolean
    last_checked_at: string
    verification_details: VerificationDetails | null
    saved: boolean
    save_error?: string
}

/**
 * AddDomainResponse is returned by POST /api/mail/domains. Enrollment with
 * the provider has already succeeded by the time this is returned — Records
 * carries the DNS the admin must publish, so a client never has to make a
 * second round trip just to learn what to show.
 */
export interface AddDomainResponse {
    id: string
    domain: string
    records?: OutboundCheckResult
}

/**
 * WebhookURLsResponse is returned by GET /api/mail/domains/{id}/webhook-urls:
 * the provider-facing inbound and bounce webhook URLs for the domain.
 */
export interface WebhookURLsResponse {
    inbound: string
    bounces: string
}

/**
 * WebhookAckResponse acknowledges a provider webhook delivery. Status is
 * "ok" (inbound accepted), or for bounces "processed" / "ignored" (unknown
 * message).
 */
export interface WebhookAckResponse {
    status: string
}
